import { ReactElement, useEffect, useState } from "react";

import "./view-history.scss";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import {
  ADD_TO_BREADCRUMB,
  LOADING,
  REMOVE_FROM_BREADCRUMB,
} from "../../store/actions";
import { AxiosResponse } from "axios";
import instance from "../../store/axios";
import { useNavigate, useParams } from "react-router-dom";
import { ColumnsType } from "antd/es/table";
import clsx from "clsx";
import CustomTable from "../../components/custom-table/custom-table";
import moment from "moment";
import BreadcrumbComponent from "../../components/breadcrumb-component/breadcrumb-component";

const ViewHistory: React.FC<any> = (): ReactElement => {
  const { kycReviewId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [versionsData, setVersionsData] = useState<any>([]);

  useEffect(() => {
    getHistoryKyc();
    dispatch({
      type: ADD_TO_BREADCRUMB,
      payload: {
        breadcrumb: [
          {
            text: `View history`,
            type: "text",
          },
        ],
      },
    });

    return () => {
      dispatch({
        type: REMOVE_FROM_BREADCRUMB,
        payload: {
          breadcrumb: [
            {
              text: `View history`,
            },
          ],
        },
      });
    };
  }, []);

  const getHistoryKyc = async (): Promise<void> => {
    dispatch({ type: LOADING, payload: true });

    try {
      const response: AxiosResponse<any> = await instance.get(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/kyc-review/${kycReviewId}/full-history`
      );
      setVersionsData(response?.data?.data);
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      localStorage.setItem(
        "star_prev_url",
        `/customers/view-history/${kycReviewId}`
      );
    }

    dispatch({ type: LOADING, payload: false });
  };

  const columns: ColumnsType<any> = [
    {
      title: "Group",
      dataIndex: "group",
      key: "group",
      sorter: (a, b) => a.group - b.group,
      fixed: "left",
    },
    {
      title: "Created at",
      dataIndex: "reviewedAt",
      key: "reviewedAt",
      width: 200,
      sorter: (a, b) => a.reviewedAt?.length - b.reviewedAt?.length,
      fixed: "left",
      render: (text: string) => (
        <span>
          {text ? moment(text).format("DD MMM, YYYY hh:mm A") : " - "}
        </span>
      ),
    },
    {
      title: "Risk assessment",
      dataIndex: "riskAssessment",
      key: "riskAssessment",
      sorter: (a, b) => a.riskAssessment?.length - b.riskAssessment?.length,
      render: (_, record) => <span>{record?.riskAssessment || " - "}</span>,
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      width: 200,
      sorter: (a, b) => a.notes.length - b.notes.length,
      render: (text: string) => <span>{text ? text : " - "}</span>,
    },
    {
      title: "Message details",
      dataIndex: "message",
      key: "message",
      width: 200,
      render: (_, record) => (
        <span>
          {record?.messages?.length
            ? record?.messages?.map((item) => item?.message)?.join(", ")
            : " - "}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: (a, b) => a.status.length - b.status.length,
      render: (text, record) => (
        <span
          className={clsx(
            "flex rounded-md text-xs px-3 py-1 items-center justify-center h-[35px]",
            record?.status === "Approved"
              ? "bg-gray-600 text-green-500"
              : record?.status === "FEEDBACK_REQUIRED"
              ? "bg-yellow-500 text-white"
              : "bg-red-200 text-white"
          )}
        >
          {text === "FEEDBACK_REQUIRED" ? "Feedback Required" : text || " - "}
        </span>
      ),
    },
    {
      title: "Action by",
      dataIndex: "actionBy",
      width: 180,
      key: "actionBy",
      sorter: (a, b) => a.actionBy?.length - b.actionBy?.length,
      render: (_, record) => <span>{record?.reviewedBy?.name || " - "}</span>,
    },
    {
      title: "AML Risk assessment",
      dataIndex: "amlRiskAssessment",
      key: "amlRiskAssessment",
      // width: 200,
      render: (_, record) =>
        record?.amlRiskAssessmentId ? (
          <a href={record?.amlRiskAssessment?.storagePath} target="_blank">
            Risk assessment
          </a>
        ) : (
          <span>_</span>
        ),
    },
    {
      title: "Management approval",
      dataIndex: "managementApproval",
      key: "managementApproval",
      // width: 200,
      render: (_, record) =>
        record?.managementApprovalId ? (
          <a href={record?.managementApproval?.storagePath} target="_blank">
            Management approval
          </a>
        ) : (
          <span>_</span>
        ),
    },
    {
      title: "World clearance",
      dataIndex: "worldClearance",
      key: "worldClearance",
      // width: 200,
      render: (_, record) =>
        record?.worldClearanceId ? (
          <a href={record?.worldClearance?.storagePath} target="_blank">
            World clearance
          </a>
        ) : (
          <span>_</span>
        ),
    },
  ];

  return (
    <section className="view-history-container flex flex-col">
      <div className="header flex items-center justify-between">
        <BreadcrumbComponent />
        {/* <button className="flex items-center mb-5">
          Export
          <img src={"/images/export.svg"} alt="export-customers" />
        </button> */}
      </div>
      <div className="flex flex-col gap-9">
        {versionsData?.length
          ? versionsData?.map((item, index) => (
              <div
                key={index}
                className="flex flex-col bg-white p-5 rounded-xl"
              >
                <div className="flex items-center gap-10">
                  <h3 className="text-lg font-medium mb-5">
                    KYC submited at:{" "}
                    {moment(item?.createdAt).format("DD MMM, YYYY hh:mm A")}
                  </h3>
                  {item?.kyc?.version && (
                    <h3 className="text-lg font-medium mb-5">
                      KYC version: {item?.kyc?.version}
                    </h3>
                  )}
                  {item?.accountDocuments?.version && (
                    <h3 className="text-lg font-medium mb-5">
                      Document version:
                      {item?.accountDocuments?.version}
                    </h3>
                  )}
                </div>
                <CustomTable
                  columns={columns}
                  data={item?.feedback}
                  showPagination={false}
                />
              </div>
            ))
          : ""}
      </div>
      <div className="button flex justify-end">
        <button onClick={() => navigate("/customers/waiting-approvals")}>
          Back
        </button>
      </div>
    </section>
  );
};

export default ViewHistory;
